/**
 * 출처(Sources) 버튼: 클릭 시 해당 references를 사이드바에 표시
 * - assistant-message: 답변 하단 Sources. 탭 구조(External Ref. / Knowledge Base)로 연다
 * - response-renderer td/th: 테이블 셀 내 셀당 1개 (hideLabel). 목록으로 연다
 */
import useAnswerSideBar from "@/hooks/ai-agent/use-answer-side-bar";
import { Button } from "../ui/button";
import { Text } from "../ui/text";
import { Reference } from "./message-list";
import useAnswerSideBarStore from "@/stores/answer-side-bar";
import type { AnswerSideBarItem } from "@/stores/answer-side-bar";
import { getSourceFavicons, getSourceKey } from "@/utils/reference";
import {
  FileBadgeStack,
  dedupeBadges,
  getFileBadge,
  type FileBadgeInfo,
} from "@/components/ui/file-type-indicator";
import { hasKnowledgeContent, type KbContentData } from "@/utils/knowledge-content";
import FaviconImage from "./favicon-image";

interface ReferenceButtonProps {
  /** KB 전용 응답에서는 null 로 온다 */
  references?: Reference[] | null;
  /** true면 "Sources" 텍스트 숨김 (테이블 출처 표시용) */
  hideLabel?: boolean;
  knowledgeContent?: KbContentData | null;
  /** 웹 출처 없는 KB 전용 버튼끼리 키가 겹치지 않게 message.id 를 받는다 */
  instanceKey?: string;
}

const ReferenceButton = ({
  references,
  hideLabel,
  knowledgeContent,
  instanceKey,
}: ReferenceButtonProps) => {
  const refs = references ?? [];
  const { handleAnswerSideBarOpen } = useAnswerSideBar();
  const isOpen = useAnswerSideBarStore((state) => state.isOpen);
  /** 현재 열린 출처 그룹 식별 (토글·강조용) */
  const selectedReferenceNumber = useAnswerSideBarStore(
    (state) => state.selectedReferenceNumber
  );

  const hasKb = hasKnowledgeContent(knowledgeContent);
  if (refs.length === 0 && !hasKb) return null;

  /* 하단 버튼은 탭 모드로 열고 답변마다 KB 가 달라서, 출처 번호가 같은 표 칸 버튼이나
     다른 답변 버튼과 키가 겹치지 않게 메시지 id 를 붙인다 */
  const refsKey = getSourceKey(refs);
  const sourceKey = hideLabel ? refsKey : `${instanceKey ?? ""}:${refsKey}`;
  const sourceFavicons = getSourceFavicons(refs);
  const fileBadges: FileBadgeInfo[] = dedupeBadges(
    [...refs.map((r) => r.title), ...(knowledgeContent?.unstructuredReport ?? []).map((u) => u.title)]
      .map(getFileBadge)
      .filter((b): b is FileBadgeInfo => b !== null)
  );
  const hasImage = fileBadges.length > 0 || sourceFavicons.length > 0;
  const isActive = isOpen && selectedReferenceNumber === sourceKey;

  return (
    <Button
      onClick={() => {
        handleAnswerSideBarOpen(
          "answer-reference",
          refs.map((r) => ({
            id: r.reference_number,
            url: r.url,
            title: r.title,
            content: r.content,
          } satisfies AnswerSideBarItem)),
          !hideLabel,
          hasKb ? knowledgeContent : null,
          hideLabel ? undefined : sourceKey
        );
      }}
      className={`rounded-[8px] px-4 py-[6px] ${hasImage ? "gap-[7px]" : "justify-center"} ${
        isActive ? "bg-[#C5F7EE]" : "bg-[#F8F8F8]"
      }`}
    >
      {/* bg 강조: isOpen && sourceKey 일치 시 선택된 출처 표시 */}
      {hasImage && (
        <div className="flex items-center gap-[4px]">
          {sourceFavicons.length > 0 && (
            <div className="flex items-center">
              {sourceFavicons.map((favicon, index) => (
                <FaviconImage
                  key={favicon}
                  src={favicon}
                  alt={`source-${index}`}
                  className={`w-5 h-5 rounded-full object-cover border-2 border-white flex-shrink-0 ${
                    index > 0 ? "-ml-[6.67px]" : ""
                  }`}
                />
              ))}
            </div>
          )}
          {fileBadges.length > 0 && <FileBadgeStack badges={fileBadges} />}
        </div>
      )}
      {!hideLabel && (
        <Text variant="label-lg" style={{ fontWeight: 400, fontSize: "12px", fontStyle: "normal" }}>
          출처
        </Text>
      )}
    </Button>
  );
};

export default ReferenceButton;
